import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Custom hook to monitor Auth0 token expiration and handle renewal
 * Proactively checks token validity and redirects to login if expired
 */
export const useTokenMonitor = () => {
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      // Clear any existing interval if user is not authenticated
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Function to check token validity
    const checkTokenValidity = async () => {
      try {
        // Try to get token silently - this will fail if token is expired
        await getAccessTokenSilently({
          cacheMode: 'off' // Force token validation, not from cache
        });
        console.log('Token is still valid');
      } catch (error: any) {
        console.error('Token validation failed:', error);
        
        // If token is expired or requires login, redirect
        if (error.error === 'login_required' || 
            error.error === 'consent_required' ||
            error.error === 'interaction_required') {
          console.log('Token expired, redirecting to login...');
          
          // Clear stored auth state
          localStorage.removeItem('auth0.is.authenticated');
          
          // Redirect to login
          await loginWithRedirect({
            authorizationParams: {
              prompt: 'none' // Try silent auth first
            }
          }).catch(() => {
            // If silent auth fails, force login
            loginWithRedirect();
          });
        }
      }
    };

    // Check token immediately when authenticated
    checkTokenValidity();

    // Set up periodic token check (every 5 minutes)
    intervalRef.current = setInterval(checkTokenValidity, 5 * 60 * 1000);

    // Cleanup interval on unmount or when auth state changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);

  // Also check token on visibility change (when user returns to tab)
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('Tab became visible, checking token...');
        try {
          await getAccessTokenSilently({
            cacheMode: 'off'
          });
        } catch (error: any) {
          if (error.error === 'login_required') {
            console.log('Token expired while tab was hidden, redirecting to login...');
            localStorage.removeItem('auth0.is.authenticated');
            loginWithRedirect();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, getAccessTokenSilently, loginWithRedirect]);
};